import { useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { api } from "@/lib/api";
import { X, Mail, ExternalLink, User as UserIcon, Calendar } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("sv-SE", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

export function GmailMessageModal({ messageId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [onClose]);

  useEffect(() => {
    if (!messageId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/varmdo/gmail/messages/${messageId}`);
        if (!cancelled) setMsg(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Kunde inte ladda mail");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [messageId]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-3 sm:p-6"
      style={{
        paddingTop: "max(0.75rem, env(safe-area-inset-top))",
        paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))",
      }}
      onClick={onClose}
      data-testid="gmail-modal-backdrop"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-2xl rounded-2xl shadow-xl overflow-hidden flex flex-col"
        style={{ maxHeight: "calc(100vh - 48px)" }}
        data-testid="gmail-modal"
      >
        <header className="px-5 py-3 border-b border-[color:var(--border)] flex items-start gap-3 bg-white">
          <div className="w-9 h-9 rounded-xl bg-[#FDF2EE] text-[#E07A5F] flex items-center justify-center flex-shrink-0">
            <Mail size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#E07A5F]">
              Gmail · {msg?.owner_label || ""}
            </p>
            <h3 className="text-base font-medium leading-snug truncate">
              {msg?.subject || (loading ? "Laddar mail…" : "(Inget ämne)")}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 hover:bg-[color:var(--bg-elevated)] flex-shrink-0"
            data-testid="gmail-modal-close"
            aria-label="Stäng"
          >
            <X size={18} />
          </button>
        </header>

        <div className="px-5 py-3 border-b border-[color:var(--border)] flex flex-col gap-1 text-xs">
          <div className="flex items-center gap-2 text-[color:var(--text-secondary)]">
            <UserIcon size={12} />
            <span className="truncate">
              {msg?.from_name || msg?.from_email || ""}{" "}
              {msg?.from_email && msg?.from_name && (
                <span className="text-[color:var(--text-tertiary)]">&lt;{msg.from_email}&gt;</span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2 text-[color:var(--text-secondary)]">
            <Calendar size={12} />
            <span>{fmtDate(msg?.date_iso) || msg?.date_text || ""}</span>
          </div>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4">
          {loading ? (
            <p className="text-sm text-[color:var(--text-tertiary)] text-center mt-8">
              Hämtar mail…
            </p>
          ) : error ? (
            <p className="text-sm text-[#E07A5F]">{error}</p>
          ) : msg?.body_html ? (
            <div
              className="prose prose-sm max-w-none text-sm leading-relaxed"
              data-testid="gmail-modal-body-html"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(msg.body_html) }}
            />
          ) : (
            <pre
              className="whitespace-pre-wrap break-words text-sm leading-relaxed font-sans"
              data-testid="gmail-modal-body-text"
            >
              {msg?.body_text || msg?.snippet || "(Inget innehåll)"}
            </pre>
          )}
        </div>

        {msg?.thread_url && (
          <footer className="border-t border-[color:var(--border)] px-5 py-3 flex items-center justify-between">
            <span className="text-xs text-[color:var(--text-tertiary)]">
              {msg.is_unread ? "Markerat som oläst" : ""}
            </span>
            <a
              href={msg.thread_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[color:var(--jarvis)] hover:underline"
              data-testid="gmail-modal-open-gmail"
            >
              Öppna i Gmail <ExternalLink size={12} />
            </a>
          </footer>
        )}
      </div>
    </div>
  );
}

// XSS-skydd för Gmail-mail (kan innehålla reklam, marketing-trackers, etc.).
// DOMPurify whitelistar säkra taggar/attribut och stripar JS, on*-handlers,
// javascript:-URI, style-injektion m.m. Vi öppnar länkar i ny flik för säkerhets skull.
function sanitizeHtml(html) {
  if (!html) return "";
  return DOMPurify.sanitize(html, {
    FORBID_TAGS: ["script", "iframe", "style", "object", "embed", "form", "input"],
    FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur"],
    ADD_ATTR: ["target", "rel"],
    ALLOW_DATA_ATTR: false,
  });
}

export default GmailMessageModal;
